import { logger as LOG } from '@garnish/logger';
import { type StripeError } from '@stripe/stripe-js';

const logger = LOG.extend('Stripe');

const errorLogger = LOG.extend('Stripe Error');

/**
 * Result from attempting to confirm the payment intent.
 * `Stripe | Confirm Result`
 */
function logConfirmResult(result: unknown) {
  logger.info('Confirm Result', result);
}

/**
 * Customer canceled the payment request.
 * `Stripe | Cancelation`
 */
function logCancelation() {
  logger.info('Cancelation');
}

/**
 * Error submitting the elements form from Stripe.
 * `Stripe Error | Submit Error`
 */
function logSubmitError(error?: StripeError | Error) {
  errorLogger.error('Submit Error', error);
}

/**
 * Error creating a payment method from Stripe.
 * `Stripe Error | Create Payment Method Error`
 */
function logCreatePaymentMethodError(error?: StripeError | Error) {
  errorLogger.error('Create Payment Method Error', error);
}

/**
 * Error when confirm is called but Stripe is not initialized.
 * `Stripe Error | Stripe Not Initialized Error`
 */
function logStripeNotInitializedError() {
  errorLogger.error('Stripe Not Initialized Error');
}

/**
 * Error when confirming a payment intent from Stripe.
 * `Stripe Error | Confirm Error`
 */
function logConfirmError(error?: StripeError | Error | string) {
  errorLogger.error('Confirm Error', error);
}

/**
 * Error when no payment method was created from Stripe.
 * `Stripe Error | No Payment Method Created Error`
 */
function logNoPaymentMethodCreatedError() {
  errorLogger.error('No Payment Method Created Error');
}

/**
 * Generic error during the Stripe flow.
 * Includes failing to create a payment intent (backend call failure).
 * `Stripe Error | Generic Error`
 */
function logGenericError(error?: Error) {
  errorLogger.error('Generic Error', error);
}

/**
 * Error or timeout while creating a Radar Session for registration fraud checks.
 * `Stripe Error | Radar Session Error`
 */
function logRadarSessionError(error?: RadarSessionError) {
  errorLogger.error('Radar Session Error', error);
}

function logStripeLoadError(error?: Error | string) {
  errorLogger.error('Stripe Load Error', {
    error:
      error instanceof Error
        ? { name: error.name, message: error.message, stack: error.stack }
        : error,
    message: String(error),
    hasStripeWindow: hasStripeWindow(),
    hasStripeScript: hasStripeScript(),
  });
}

function hasStripeWindow() {
  if (typeof window === 'undefined') {
    return false;
  }

  return (window as Window & { Stripe?: unknown }).Stripe !== undefined;
}

function hasStripeScript() {
  if (typeof document === 'undefined') {
    return false;
  }

  return document.querySelectorAll('script[src^="https://js.stripe.com"]').length > 0;
}

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * The web and native Stripe SDKs report errors with different shapes, so the
 * logger only relies on the fields both provide.
 */
type RadarSessionError = Error | string | { code?: string; message?: string };

// ─── Export ──────────────────────────────────────────────────────────────────

export const StripeLogger = {
  logConfirmResult,
  logCancelation,
  logSubmitError,
  logCreatePaymentMethodError,
  logStripeNotInitializedError,
  logConfirmError,
  logNoPaymentMethodCreatedError,
  logGenericError,
  logRadarSessionError,
  logStripeLoadError,
};
